import React from 'react'
import { useInView } from 'react-intersection-observer'

import classnames from 'classnames'

import type { LinkProps } from '~/components/links'
import { resolveLink } from '~/components/serializers'

import Button, { ButtonVariants } from '../Button/Button'
import { Col, Grid, Row } from '../Grid'
import type { ImageProps } from '../Image/Image'
import Image from '../Image/Image'
import Spacer, { SpacerSizes } from '../Spacer/Spacer'
import Text, { TextSizes } from '../Text/Text'

import styles from './KeyMessageBanner.module.css'

export enum ImageAlignmentVariants {
  left = 'left',
  right = 'right',
  full = 'full',
}

export interface KeyMessageBannerProps {
  imageAlign?: ImageAlignmentVariants
  images: ImageProps | any
  title: string
  subtitle?: string
  textMask?: boolean
  testID?: string
  link?: LinkProps
}

export default function KeyMessageBanner({
  imageAlign,
  images,
  title,
  subtitle,
  textMask,
  testID,
  link,
}: KeyMessageBannerProps) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })

  return (
    <div
      className={classnames(styles.container, inView && styles.inview)}
      ref={ref}
    >
      <Grid>
        <Row>
          {Array.isArray(images) && images.length > 1 ? (
            <>
              <Col variant="col-1/4--md">
                <div className={styles.imageContainer}>
                  <Image
                    asset={images[0].image}
                    alt={images[0].alt}
                    objectFit="cover"
                    sizes="10vw"
                  />
                </div>
              </Col>
              <Col variant="col-1/2--md">
                <div
                  data-testid={testID && `${testID}-image-double`}
                  className={classnames(styles.inner, styles.center)}
                >
                  <div className={styles.animatedText}>
                    <Text Tag="h2" size={TextSizes.keyBannerTitle}>
                      {title}
                    </Text>
                    <Text Tag="p" size={TextSizes.secondaryTitleLrg}>
                      {subtitle}
                    </Text>
                  </div>
                </div>
              </Col>
              <Col variant="col-1/4--md">
                <div className={styles.imageContainer}>
                  <Image
                    asset={images[1].image}
                    alt={images[1].alt}
                    objectFit="cover"
                  />
                </div>
              </Col>
            </>
          ) : (
            !imageAlign ||
            (imageAlign === ImageAlignmentVariants.left && (
              <>
                <Col variant="col-1/3--md">
                  <div className={styles.imageContainer}>
                    <Image
                      asset={images.image}
                      alt={images.alt}
                      objectFit="cover"
                    />
                  </div>
                </Col>
                <Col variant="col-2/3--md">
                  <div
                    data-testid={testID && `${testID}-image-left`}
                    className={classnames(styles.inner, styles.right)}
                  >
                    <div className={styles.animatedText}>
                      <Text Tag="h2" size={TextSizes.keyBannerTitle}>
                        {title}
                      </Text>
                      <Text Tag="p" size={TextSizes.secondaryTitleLrg}>
                        {subtitle}
                      </Text>
                    </div>
                  </div>
                </Col>
              </>
            )) ||
            (imageAlign === ImageAlignmentVariants.right && (
              <>
                <Col variant="col-2/3--md">
                  <div
                    data-testid={testID && `${testID}-image-right`}
                    className={styles.inner}
                  >
                    <div className={styles.animatedText}>
                      <Text Tag="h2" size={TextSizes.keyBannerTitle}>
                        {title}
                      </Text>
                      <Text Tag="p" size={TextSizes.secondaryTitleLrg}>
                        {subtitle}
                      </Text>
                    </div>
                  </div>
                </Col>
                <Col variant="col-1/3--md">
                  <div className={styles.imageContainer}>
                    <Image
                      asset={images.image}
                      alt={images.alt}
                      objectFit="cover"
                    />
                  </div>
                </Col>
              </>
            )) ||
            (imageAlign === ImageAlignmentVariants.full && (
              <Col variant="col-full-width">
                <div
                  data-testid={testID && `${testID}-image-full`}
                  className={classnames(styles.inner, styles.full)}
                >
                  <div className={styles.imageFullContainer}>
                    <div>
                      <Image
                        asset={images.image}
                        alt={images.alt}
                        objectFit="cover"
                        sizes="100vw"
                      />
                    </div>
                  </div>
                  <div
                    className={classnames(styles.imageFullContent, {
                      [styles.mask]: textMask,
                    })}
                    data-testid={testID && `${testID}-text-mask`}
                  >
                    <div className={styles.animatedText}>
                      <Text Tag="h2" size={TextSizes.keyBannerTitle}>
                        {title}
                      </Text>
                      <Text Tag="p" size={TextSizes.secondaryTitleLrg}>
                        {subtitle}
                      </Text>
                      {link &&
                        (link.externalLink ||
                          link.internalLink ||
                          link.launchForm) && (
                          <>
                            <Spacer size={SpacerSizes.md} />
                            <Button
                              link={resolveLink(link)}
                              testID={testID}
                              gaTrack={link.gaTrack}
                              gaTrackEventName={link.gaTrackEventName}
                              phCTASource={link?.phCTASource}
                              phEventName={link?.phEventName}
                              phInteractionLocation={
                                link?.phInteractionLocation
                              }
                              phWebProduct={link?.phWebProduct}
                              launchForm={link.launchForm}
                              variant={ButtonVariants.primaryLight}
                            >
                              {link?.text}
                            </Button>
                          </>
                        )}
                    </div>
                  </div>
                </div>
              </Col>
            ))
          )}
        </Row>
      </Grid>
    </div>
  )
}
